import { useMemo } from 'react';
import {
	COMMAND_PALETTE_SECTIONS,
	COMMAND_PALETTE_SECTIONS_PRIORITY,
} from '@atlassian/jira-command-palette-common/src/common/constants.tsx';
import type { CommandSections } from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { useRegisterCommandSections } from '@atlassian/jira-command-palette/src/controllers/use-register-sections/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { messages } from './messages.tsx';
import type { CommandSectionsWithMessage } from './types.tsx';

export const initialSectionsWithoutAppify: CommandSectionsWithMessage = {
	[COMMAND_PALETTE_SECTIONS.containerNavigation]: {
		message: messages.containerNavigationSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.containerNavigation,
		actionCategory: 'containerNavigation',
	},
	[COMMAND_PALETTE_SECTIONS.siteNavigation]: {
		message: messages.siteNavigationSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.siteNavigation,
		actionCategory: 'siteNavigation',
	},
	[COMMAND_PALETTE_SECTIONS.quickActions]: {
		message: messages.quickActionsSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.quickActions,
		actionCategory: 'quickActions',
	},
	[COMMAND_PALETTE_SECTIONS.personalSettings]: {
		message: messages.personalSettingsSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.personalSettings,
		actionCategory: 'personalSettings',
	},
	[COMMAND_PALETTE_SECTIONS.help]: {
		message: messages.helpSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.help,
		actionCategory: 'help',
	},
	[COMMAND_PALETTE_SECTIONS.jiraSettings]: {
		message: messages.jiraSettingsSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.jiraSettings,
		actionCategory: 'jiraSettings',
	},
	[COMMAND_PALETTE_SECTIONS.atlassianAdmin]: {
		message: messages.atlassianAdminSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.atlassianAdmin,
		actionCategory: 'atlassianAdmin',
	},
	[COMMAND_PALETTE_SECTIONS.quickSearch]: {
		message: messages.quickSearchSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.quickSearch,
		actionCategory: 'quickSearch',
	},
	[COMMAND_PALETTE_SECTIONS.forge]: {
		message: messages.forgeSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.forge,
		actionCategory: 'forge',
	},
};

export const initialSectionsWithAppify = {
	[COMMAND_PALETTE_SECTIONS.containerNavigation]: {
		message: messages.containerNavigationSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.containerNavigation,
		actionCategory: 'containerNavigation',
	},

	[COMMAND_PALETTE_SECTIONS.siteNavigation]: {
		message: messages.siteNavigationSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.siteNavigation,
		actionCategory: 'siteNavigation',
	},

	[COMMAND_PALETTE_SECTIONS.quickActions]: {
		message: messages.quickActionsSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.quickActions,
		actionCategory: 'quickActions',
	},

	[COMMAND_PALETTE_SECTIONS.personalSettings]: {
		message: messages.personalSettingsSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.personalSettings,
		actionCategory: 'personalSettings',
	},

	[COMMAND_PALETTE_SECTIONS.help]: {
		message: messages.helpSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.help,
		actionCategory: 'help',
	},

	[COMMAND_PALETTE_SECTIONS.jiraSettings]: {
		message: messages.jiraSettingsSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.jiraSettings,
		actionCategory: 'jiraSettings',
	},

	[COMMAND_PALETTE_SECTIONS.atlassianAdmin]: {
		message: messages.atlassianAdminSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.atlassianAdmin,
		actionCategory: 'atlassianAdmin',
	},

	[COMMAND_PALETTE_SECTIONS.quickSearch]: {
		message: messages.quickSearchSection,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.quickSearch,
		actionCategory: 'quickSearch',
	},

	[COMMAND_PALETTE_SECTIONS.forge]: {
		message: messages.forgeSectionAppify,
		priority: COMMAND_PALETTE_SECTIONS_PRIORITY.forge,
		actionCategory: 'forge',
	},
};

export const useRegisterSections = (): void => {
	const { formatMessage } = useIntl();

	const initialSections = fg('product-terminology-refresh')
		? initialSectionsWithAppify
		: initialSectionsWithoutAppify;

	const sections: CommandSections = useMemo(
		() =>
			Object.entries(initialSections).reduce((acc, [key, entry]) => {
				acc[key] = {
					name: formatMessage(entry.message),
					priority: entry.priority,
					actionCategory: entry.actionCategory,
				};

				return acc;
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			}, {} as CommandSections),
		[formatMessage, initialSections],
	);

	useRegisterCommandSections(sections);
};
